import { createContext, useContext, useEffect, useState } from "react";

const TenantContext = createContext(null);

const TENANT_KEY = "ybp_tenant_v1";
const USER_THEME_KEY = "ybp_user_theme_v1";

const DEFAULT_TENANT = {
  workspaceName: "Your Business Portal",
  primaryColor: "#2563eb",
  defaultTheme: "light", // "light" | "dark"
  logo: "", // data URI; empty means use default Square icon
  onboarded: false,
};

function loadTenant() {
  if (typeof window === "undefined") return DEFAULT_TENANT;
  try {
    // Demo mode: tenant branding is per-session only — auto-clears on tab close.
    const raw = sessionStorage.getItem(TENANT_KEY);
    if (raw) return { ...DEFAULT_TENANT, ...JSON.parse(raw) };
  } catch (_e) {
    // ignore
  }
  return DEFAULT_TENANT;
}

function loadUserTheme() {
  if (typeof window === "undefined") return null;
  try {
    const v = sessionStorage.getItem(USER_THEME_KEY);
    if (v === "light" || v === "dark") return v;
  } catch (_e) {
    // ignore
  }
  return null;
}

function hexToHSLString(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = 0;
  let sat = 0;
  const lum = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = lum > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hue = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        hue = (b - r) / d + 2;
        break;
      default:
        hue = (r - g) / d + 4;
    }
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(
    lum * 100
  )}%`;
}

export function ThemeProvider({ children }) {
  const [tenant, setTenant] = useState(loadTenant);
  const [userTheme, setUserThemeState] = useState(loadUserTheme); // null when following tenant default

  const effectiveTheme = userTheme || tenant.defaultTheme;

  // Apply theme class
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", effectiveTheme === "dark");
    root.style.colorScheme = effectiveTheme;
  }, [effectiveTheme]);

  // Apply primary color as CSS variable overrides
  useEffect(() => {
    const root = document.documentElement;
    const hsl = hexToHSLString(tenant.primaryColor);
    root.style.setProperty("--primary", hsl);
    root.style.setProperty("--ring", hsl);
    root.style.setProperty("--accent", hsl);
  }, [tenant.primaryColor]);

  // Keep the browser tab title in sync with the tenant's workspace name.
  // Runs after any tenant update so URL params, third-party scripts, or
  // preview overlays cannot replace the branding.
  useEffect(() => {
    const name = tenant.workspaceName || "Your Business Portal";
    document.title = name;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", `${name} — operations portal.`);
  }, [tenant.workspaceName]);

  const saveTenant = (patch) => {
    setTenant((prev) => {
      const next = { ...prev, ...patch };
      try {
        sessionStorage.setItem(TENANT_KEY, JSON.stringify(next));
      } catch (_e) {
        // ignore
      }
      return next;
    });
  };

  const setUserTheme = (t) => {
    setUserThemeState(t);
    try {
      if (t === "light" || t === "dark") {
        sessionStorage.setItem(USER_THEME_KEY, t);
      } else {
        sessionStorage.removeItem(USER_THEME_KEY);
      }
    } catch (_e) {
      // ignore
    }
  };

  const toggle = () => {
    setUserTheme(effectiveTheme === "dark" ? "light" : "dark");
  };

  const resetUserTheme = () => setUserTheme(null);

  return (
    <TenantContext.Provider
      value={{
        tenant,
        saveTenant,
        theme: effectiveTheme,
        userTheme,
        setUserTheme,
        toggle,
        resetUserTheme,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  const { theme, toggle, setUserTheme, userTheme } = ctx;
  return { theme, toggle, setTheme: setUserTheme, userTheme };
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used inside ThemeProvider");
  return {
    tenant: ctx.tenant,
    saveTenant: ctx.saveTenant,
    resetUserTheme: ctx.resetUserTheme,
  };
}
